import React, { useState, useEffect, useRef } from 'react';
import { useClient } from 'jsonapi-react';
import { Loader, LoaderSpinner } from '@/components/custom/loader';
import toast from 'react-hot-toast';
import { EmptyState } from '@/components/custom/empty-state';
import { CourseClassroomCommentsCard } from './course-classroom-comments-card';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { PostCardWrapper, PostCommentList } from '@/components/custom/post';
import { confirm } from '@/lib/confirm';
import { FiPaperclip, FiSend, FiX } from 'react-icons/fi';
import { CardIcon } from '@/components/ui/card';
import clsx from 'clsx';
import { Tooltip } from 'react-tooltip';
import warning from '@/assets/images/icons/warning.svg';
import useAuthorityLevel from '@/hooks/useAuthorityLevel';
import api from '@/services/api';
import { Button } from '@/components/ui/button';

export function CourseClassroomComments(props) {
  const { t } = useTranslation();
  const { selectedItemId } = props;
  const client = useClient();
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [commentsMeta, setCommentsMeta] = useState({});
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const authorityLevel = useAuthorityLevel();
  const isStudent = authorityLevel === 'student';
  const [isSubmittingFile, setIsSubmittingFile] = useState(false);

  const [attachments, setAttachments] = useState([]);

  const videoLink =
    text.match(/https?:\/\/[^\s]+/gi)?.find(url => {
      try {
        const hostname = new URL(url).hostname.toLowerCase();

        return hostname.includes('youtube.com') || hostname.includes('youtu.be') || hostname.includes('vimeo.com');
      } catch {
        return false;
      }
    }) ?? null;

  const hasVideoLink = !!videoLink;

  const handleSubmitFile = async () => {
    if (attachments.length >= 5) {
      toast.error('Você pode anexar no máximo 5 arquivos por comentário.');
      return;
    }
    setIsSubmittingFile(true);

    const tempId = crypto.randomUUID();
    const controller = new AbortController();

    const attachmentPreview = {
      tempId,
      name: file?.name || videoLink,
      status: 'loading',
      controller
    };

    setAttachments(prev => [...prev, attachmentPreview]);

    const formData = new FormData();

    if (file) {
      formData.append('attachment_type', 'file');
      formData.append('file', file);
    }

    if (hasVideoLink) {
      formData.append('attachment_type', 'video_link');
      formData.append('url', videoLink);
    }

    try {
      const { data } = await api.post('attachments', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        signal: controller.signal
      });

      setAttachments(prev =>
        prev.map(item =>
          item.tempId === tempId
            ? {
                ...item,
                id: data.attributes.id,
                name: data.attributes?.['file-name'] || data?.attributes?.url,
                status: 'success',
                controller: undefined
              }
            : item
        )
      );
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        setAttachments(prev => prev.filter(item => item.tempId !== tempId));
        return;
      }

      setAttachments(prev =>
        prev.map(item =>
          item.tempId === tempId
            ? {
                ...item,
                status: 'error',
                controller: undefined
              }
            : item
        )
      );

      toast.error('Erro ao fazer upload do arquivo!');
    } finally {
      setFile(null);

      if (videoLink) {
        setText(text.replace(videoLink, '').trim());
        setIsSubmittingFile(false);
      }
    }
  };

  const deleteAttachment = async attachmentId => {
    return api.delete(`/attachments/${attachmentId}`);
  };

  const handleRemoveAttachment = async attachment => {
    // Upload em andamento
    if (attachment.status === 'loading') {
      attachment.controller?.abort();

      setAttachments(prev => prev.filter(item => item.tempId !== attachment.tempId));

      return;
    }

    // Upload com erro
    if (attachment.status === 'error') {
      setAttachments(prev => prev.filter(item => item.tempId !== attachment.tempId));

      return;
    }

    // Upload concluído
    if (attachment.status === 'success') {
      try {
        await deleteAttachment(attachment.id);

        setAttachments(prev => prev.filter(item => item.tempId !== attachment.tempId));
      } catch (error) {
        toast.error('Erro ao remover arquivo');
      }
    }
  };

  const createComment = async () => {
    const { error } = await client.mutate('comments', {
      ...(text && { body: text }),
      'learning-system-item-id': selectedItemId,
      'attachment-ids': attachments.filter(a => a.status === 'success').map(a => a.id)
    });

    if (error) {
      toast.error(t('toast.errorCreateComment'));
    } else {
      toast.success(t('toast.successCreateComment'));
      setAttachments([]);
      getComments(selectedItemId);
    }
  };

  const getComments = async selectedItemId => {
    setLoading(true);
    const url = `comments?filter[learning_system_item_id]=${selectedItemId}`;
    const { data, error, meta } = await client.fetch(url);

    setLoading(false);
    if (error) {
      toast.error(t('toast.errorGetComment'));
    } else {
      setComments(data);
      setCommentsMeta(meta);
    }
  };

  const deleteComment = async (commentToDelete, setIsCommentLoading) => {
    const confirmed = await confirm({
      title: 'Deseja mesmo excluir este comentário?',
      description: 'Esta ação não poderá ser desfeita',
      confirmText: 'Sim',
      cancelText: 'Não'
    });
    if (confirmed) {
      if (setIsCommentLoading) setIsCommentLoading(true);

      try {
        const { error } = await client.delete(['comments', commentToDelete?.id]);

        if (error) {
          toast.error(t('toast.errorDeleteComment'));
        } else {
          toast.success(t('toast.successDeleteComment'));
          getComments(selectedItemId);
        }
      } catch (e) {
        toast.error(`Erro ao excluir mensagem: ${e.message || e}`);
        console.error('Erro na exclusão:', e);
      } finally {
        if (setIsCommentLoading) setIsCommentLoading(false);
      }
    }
  };

  useEffect(() => {
    if (selectedItemId) {
      getComments(selectedItemId);
    }
  }, [selectedItemId]);

  const commentsFiltered = comments.filter(comment => comment['comment-parent-id'] === null);

  const getLimitMessage = () => {
    if (!isStudent) return;

    const sendMessageLimit = commentsMeta?.['daily-comments-limit']; // Limite de mensagens por dia
    const messagesSentToday = commentsMeta?.['daily-comments-count']; // Quantidade de envios

    if (messagesSentToday >= sendMessageLimit) {
      return (
        <p className="w-full text-sm text-[#FF726C]">
          Hoje <b>você já enviou todas as suas mensagens para esta aula </b>.
        </p>
      );
    } else if (messagesSentToday > 0) {
      return (
        <p className="w-full text-sm text-[#EE8445]">
          Faltam <b>{sendMessageLimit - messagesSentToday}</b> mensagens para o limite de hoje nesta aula ({messagesSentToday}/{sendMessageLimit}).
        </p>
      );
    } else {
      return (
        <p className="w-full text-sm text-success">
          Nesta aula você pode enviar <b>até {sendMessageLimit} mensagens por dia</b>. O limite é renovado diariamente.
        </p>
      );
    }
  };

  const hasInvalidVideoLink = text => {
    const urlRegex = /(https?:\/\/[^\s]+)/gi;

    const urls = text.match(urlRegex);

    if (!urls?.length) {
      return false;
    }

    const allowedHosts = ['youtube.com', 'www.youtube.com', 'youtu.be', 'vimeo.com', 'www.vimeo.com'];

    return urls.some(url => {
      try {
        const hostname = new URL(url).hostname.toLowerCase();

        return !allowedHosts.includes(hostname);
      } catch {
        return true;
      }
    });
  };

  // Se o usuário colocar apenas o link do vídeo sem comentário, o envio do link do vídeo é feito
  useEffect(() => {
    if (hasVideoLink && text.trim() === videoLink) {
      handleSubmitFile();
    }
    if (file) {
      handleSubmitFile();
    }
  }, [text, file]);

  const sentAllMessages = isStudent && commentsMeta && commentsMeta['daily-comments-count'] >= commentsMeta['daily-comments-limit'];

  const showLinkWarning = hasInvalidVideoLink(text);

  return (
    <PostCardWrapper>
      <div className="flex w-full flex-col items-start gap-2 rounded-lg bg-neutral-bright p-3">
        <div className="flex w-full gap-2">
          <Input
            className="bg-bright"
            placeholder={t('textsCommon.placeholderSendYourComment')}
            aria-label={t('textsCommon.placeholderSendYourComment')}
            value={text}
            disabled={loading || sentAllMessages || isSubmittingFile}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => {
              if (sentAllMessages) return;
              if (e.key === 'Enter') {
                createComment(text);
                setText('');
              }
            }}
            data-tooltip-id="video-link-tooltip"
          />
          {showLinkWarning && (
            <Tooltip
              id="video-link-tooltip"
              isOpen
              place="top"
              opacity={1}
              className="tooltip flex max-w-[300px] items-center gap-1.5 sm:max-w-full"
              border="1px solid var(--color-neutral-lightest)"
              style={{
                backgroundColor: 'var(--color-neutral-bright)',
                color: 'var(--color-neutral-darker)'
              }}
            >
              <img
                src={warning}
                alt="Alerta"
                className="h-3 w-3"
              />{' '}
              Atenção! Você só pode enviar links de vídeo do Youtube ou Vimeo
            </Tooltip>
          )}{' '}
          <FiPaperclip
            className={`shrink-0 text-lg text-primary ${sentAllMessages ? 'opacity-50' : 'cursor-pointer'}`}
            onClick={() => {
              if (sentAllMessages) return;

              fileInputRef.current?.click();
            }}
            data-tooltip-id="attach-file-tooltip"
          />
          <Tooltip
            id="attach-file-tooltip"
            className="tooltip"
            opacity={1}
            border="1px solid var(--color-neutral-lightest)"
            style={{ backgroundColor: 'var(--color-neutral-bright)', color: 'var(--color-neutral-darker)' }}
          >
            Anexar arquivo
          </Tooltip>
          <Input
            type="file"
            name="file"
            id="file"
            className="hidden"
            ref={fileInputRef}
            accept="audio/*, image/*, application/pdf, .doc, .docx, .txt, .xls, .xlsx, .csv"
            onChange={e => {
              const file = e.target.files[0];

              const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

              if (file.size > MAX_FILE_SIZE) {
                toast.error('O arquivo deve ter no máximo 10 MB.');
                e.target.value = '';
                return;
              }

              setFile(file);
              e.target.value = '';
            }}
          />
          <Button
            size="icon"
            type="button"
            disabled={loading || sentAllMessages}
            className="shrink-0"
            onClick={() => {
              if (sentAllMessages) return;
              createComment();
              setText('');
            }}
          >
            <FiSend />
          </Button>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2">
          <p className="order-1 text-xs text-neutral md:order-2 md:ml-auto">{t('textsCommon.textPressEnter')}</p>

          <div className="order-2 md:order-1">{getLimitMessage()}</div>
        </div>

        {attachments.length > 0 && (
          <div className="mt-2 flex w-full flex-col gap-2 overflow-y-auto">
            {attachments.map(file => (
              <div
                key={file.tempId}
                className="flex w-full items-center justify-between gap-2 rounded border border-neutral-lightest bg-white p-1"
              >
                <div className="flex w-full items-center justify-between gap-1 overflow-hidden text-sm text-neutral">
                  <div className={clsx('flex items-center gap-1.5', file.status === 'error' ? 'max-w-[70%] md:max-w-[55%]' : 'max-w-[85%]')}>
                    {file.status === 'loading' && (
                      <LoaderSpinner
                        text=""
                        className="w-6"
                      />
                    )}

                    <span className={`overflow-hidden text-ellipsis text-nowrap ${file.status === 'error' ? 'text-danger' : file?.status === 'success' ? 'text-success' : ''}`}>{file.name}</span>
                  </div>

                  {file.status === 'loading' && <span>Enviando...</span>}

                  {file.status === 'error' && <span className="text-nowrap text-danger">Falha no upload</span>}
                </div>

                <CardIcon>
                  <FiX
                    className="cursor-pointer text-lg text-primary"
                    onClick={() => handleRemoveAttachment(file)}
                  />
                </CardIcon>
              </div>
            ))}
          </div>
        )}
      </div>

      <PostCommentList className="mb-0 border-t-0 pt-0">
        {loading ? (
          <Loader />
        ) : commentsFiltered.length > 0 ? (
          commentsFiltered.map(c => (
            <CourseClassroomCommentsCard
              key={c.id}
              comment={c}
              comments={comments}
              deleteCommentFromParent={deleteComment}
              learningSystemItemId={selectedItemId}
              sendAllMessages={sentAllMessages}
              setCommentsMeta={setCommentsMeta}
            />
          ))
        ) : (
          <EmptyState
            type="content"
            text={t('emptyState.lmsCommentNot')}
            variant="plain"
          />
        )}
      </PostCommentList>
    </PostCardWrapper>
  );
}
